// applications query-key factories (TanStack Query)
// The ONLY thing another module may import from here.

/**
 * The applicant's own dossiers (`/acquire/applications/…`).
 *
 * A separate cache root from `rounds`, and not a branch of it. Same API prefix, different entity and
 * different owner: a round is configuration an administrator writes, an application is one
 * applicant's answers to it. Nesting these under `roundKeys` would mean a staff round save
 * invalidating an applicant's draft, and an applicant's autosave invalidating the round catalogue —
 * two caches that must not clear each other.
 */
export const applicationKeys = {
  all: ["acquire", "applications"] as const,
  lists: () => [...applicationKeys.all, "list"] as const,
  /** `GET /acquire/applications/` — every dossier the signed-in founder owns (the espace list). */
  list: (locale: string) => [...applicationKeys.lists(), { locale }] as const,
  details: () => [...applicationKeys.all, "detail"] as const,
  /**
   * One dossier. Keyed by LOCALE as well as id, because `GET /applications/{id}/?locale=` localizes
   * the schema, labels, options and legal copy server-side — two locales are two different payloads
   * and must not share an entry. Same reasoning as `publicRoundKeys.detail`.
   */
  detail: (id: string, locale: string) =>
    [...applicationKeys.details(), id, { locale }] as const,
  summaries: () => [...applicationKeys.all, "summary"] as const,
  /**
   * A DIFFERENT cache entry from `detail(id, locale)`, deliberately — same endpoint, but the espace
   * feature parses it into a much LEANER shape (progress/missing-items/document counts only, no
   * full applicant/schema contract). Sharing the key would let the espace screen's lean parse sit
   * under the same entry the dossier wizard reads, and a wizard mount served that stale, wrong-shaped
   * value would blank the form until the next refetch.
   */
  summary: (id: string, locale: string) =>
    [...applicationKeys.summaries(), id, { locale }] as const,
  informationRequestLists: () =>
    [...applicationKeys.all, "information-requests"] as const,
  /** `GET /applications/{id}/information-requests/` — the candidate's own open/past requests
   * (US-E4-04). Not locale-keyed: `subject_label`/`note` come back already in whatever locale the
   * candidate's own dossier is in, no `?locale=` param on this endpoint. */
  informationRequests: (applicationId: string) =>
    [...applicationKeys.informationRequestLists(), applicationId] as const,
  revisions: () => [...applicationKeys.all, "revision"] as const,
  /** A LEAN read of just `{id, revision}` off `GET /applications/{id}/` — its own cache entry,
   * not `detail`/`summary`, for the same reason those two don't share one either: a different
   * parse of the same endpoint under someone else's key risks a shape collision. */
  revision: (id: string) => [...applicationKeys.revisions(), id] as const,
  /**
   * Staff-side review reads (`/operations/...`) — a separate branch from `lists`/`details`
   * (the applicant's own dossiers), keyed by ROUND rather than application id, since these are
   * "every dossier in this round", not one founder's own list.
   */
  operations: {
    all: () => [...applicationKeys.all, "operations"] as const,
    lists: () => [...applicationKeys.operations.all(), "list"] as const,
    /** `GET /operations/applications/` for one round. Locale-keyed for the same reason as `detail`. */
    list: (roundId: string, locale: string) =>
      [...applicationKeys.operations.lists(), roundId, { locale }] as const,
    /** `GET /operations/workspace/` for one round — the sidebar rail's round name/status/counts. */
    workspace: (roundId: string, locale: string) =>
      [
        ...applicationKeys.operations.all(),
        "workspace",
        roundId,
        { locale },
      ] as const,
    queues: () => [...applicationKeys.operations.all(), "queue"] as const,
    /** `GET /operations/queue/` for one round — "À traiter". Always the full, unfiltered fetch;
     * the Tous/Signalements/Demandes en retard/Paires tabs filter it client-side (see
     * `useOperationsQueue`'s own doc comment), so there's no `cause` dimension on this key. */
    queue: (roundId: string, locale: string) =>
      [...applicationKeys.operations.queues(), roundId, { locale }] as const,
    waitingLists: () =>
      [...applicationKeys.operations.all(), "waiting"] as const,
    /** `GET /operations/waiting/` for one round — "Dossiers en attente". */
    waiting: (roundId: string, locale: string) =>
      [
        ...applicationKeys.operations.waitingLists(),
        roundId,
        { locale },
      ] as const,
    dossiers: () => [...applicationKeys.operations.all(), "dossier"] as const,
    /** `GET /operations/applications/{id}/` — the detail screen. */
    dossier: (applicationId: string, locale: string) =>
      [
        ...applicationKeys.operations.dossiers(),
        applicationId,
        { locale },
      ] as const,
    histories: () => [...applicationKeys.operations.all(), "history"] as const,
    /** `GET /operations/applications/{id}/history/` — the detail screen's "Historique" tab. */
    history: (applicationId: string, locale: string) =>
      [
        ...applicationKeys.operations.histories(),
        applicationId,
        { locale },
      ] as const,
  },
};
